import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { addHours, subHours } from 'date-fns';
import { FormProvider, useForm } from 'react-hook-form';
import clsx from 'clsx';
import moment from 'moment';
import { toast } from 'react-toastify';
import Button from '../../controls/Button';
import MultiSelectInputBox from '../MultiSelectInputBox';
import { Alert, AlertStatus, DeviceType } from '../../constants/telosair-interfaces';
import { getAnalysisData, getUsedTags, updateAlert } from '../../services/dashboard-service';
import { alertStatus, TOAST_MESSAGES } from '../../constants/global-constants';
import AlertGraph from './graph';
import AssignUsers from './AssignUsers';
import Loader from '../loader';
import { useData } from '../../context/data.context';
import {
  getBgClassNameByAlert,
  getCSSClassBasedOnPriority,
  getParameterByName,
  alertValueText
} from '../../_helpers/globalHelper';
import CustomTextArea from '../../controls/CustomTextArea';
import { getPriorityColor } from '../../_helpers/dashboardHelper';

type FORMDATA = {
  comment: string;
};

type DeviceRowProps = {
  alert: Alert;
  onClick: () => void;
};

type GraphDataAttribute = {
  value: number[][];
  color: string;
  serailNumber: number;
};
/**
 * A component to display device data in a grid.
 * @param props - Device Row Properties.
 * @returns A React element representing the DeviceRow component.
 */
export default function ExpandedAlertRow(props: DeviceRowProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { alert, onClick } = props;
  const {
    userConfigData,
    parameters,
    updateAlertData,
    alertRules,
    clientThresholdSettings,
    userConfigDataV2
  } = useData();
  const tagInputRef = useRef<any>(null);
  const [reasonChips, setReasonChips] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(true);
  const [tagList, setTagList] = useState<string[]>([]);
  const [lineChartGraphData, setLineChartGraphData] = useState<
    | {
        value: number[][];
        color: string;
        serailNumber: number;
      }[]
    | null
  >(null);

  const methods = useForm<FORMDATA>({
    mode: 'all'
  });
  const { handleSubmit, reset, watch } = methods;

  useEffect(() => {
    if (alert.tags) {
      setReasonChips(alert.tags);
    }
  }, [alert]);

  /**
   * Handles API call for activities put API.
   * @param data - Formdata.
   */
  const handleFormSubmit = async (data: FORMDATA): Promise<void> => {
    const allChips = tagInputRef.current.forceAddChip();
    try {
      if (allChips.length === 0) {
        toast.warn('Please add a cause or a reason');
        return;
      }
      setLoading(true);
      const updatedAlert = await updateAlert(
        { tags: allChips, noteTitle: data.comment || null },
        alert.id
      );
      updateAlertData(updatedAlert.data);
      toast.success(TOAST_MESSAGES.added);
      reset({ comment: '' });
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.success(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Function to get x axis labels.
   * @param timestamp - Timestamp of the axis label.
   * @returns - XAxis label.
   */
  const getFormattedXAxisLabel = (timestamp: number): any => {
    const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(timestamp).tz(timeZone);
  };

  const fetchData = useCallback(async () => {
    const timeSeriesType = getParameterByName(alert.parameter, parameters);
    if (timeSeriesType) {
      const payload = {
        sensors: [parseInt(alert.serialNumber, 10)],
        timeSeriesType: timeSeriesType.parameterName,
        startDate: subHours(new Date(alert.timestamp), 3).getTime(),
        endDate: addHours(alert.endDate ? new Date(alert.endDate) : new Date(), 3).getTime()
      };
      const { data } = await getAnalysisData(payload);
      const graphDataArray: GraphDataAttribute[] = [];
      if (data.status) {
        data.detailed.forEach((item: any) => {
          graphDataArray.push({
            value: item.data.map((graphPoint: number[]) => [
              getFormattedXAxisLabel(graphPoint[0]),
              graphPoint[1]
            ]),
            color: timeSeriesType.color,
            serailNumber: item.name
          });
        });
      }

      setLineChartGraphData(graphDataArray);
      setAnalysisLoading(false);
    } else {
      setAnalysisLoading(false);
    }
  }, [alert]);

  /**
   * Function to get old used tags.
   */
  const fetchUsedTags = async (): Promise<void> => {
    try {
      const response = await getUsedTags();
      setTagList(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchUsedTags();
  }, []);

  /**
   * Handles delete alert function.
   * @param event - Button click event.
   * @param status - Dismiss | resolved.
   */
  const handleUpdateAlert = async (event: any, status: AlertStatus): Promise<void> => {
    try {
      event.stopPropagation();
      const updatedAlert = await updateAlert({ status }, alert.id);
      updateAlertData(updatedAlert.data);
      toast.success('Alert updated successfully');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  const isThereANewTag =
    alert.tags || reasonChips.length !== 0
      ? reasonChips.sort((a: any, b: any) => b - a).toString() !==
        alert.tags?.sort((a: any, b: any) => b - a).toString()
      : false;
  const parameter = getParameterByName(alert.parameter, parameters);
  const rule = alertRules.filter((item) => item.id === alert.alertId);
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const priority = getPriorityColor(
    alert.parameter,
    alert.avgValue,
    clientThresholdSettings,
    userConfigDataV2,
    DeviceType.MIXED
  );
  return (
    <div className="border-l-2 bg-container-700 w-full">
      <div className="p-6 pt-3 box-border">
        <div>
          <button
            onClick={onClick}
            className="material-icons-outlined text-gray-300 "
            type="button"
          >
            chevron_left
          </button>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-6">
            <div className="items-center gap-3 flex h-16">
              <div
                className={`w-11 h-11 min-h-[2.75rem] min-w-[2.75rem] ${getBgClassNameByAlert(
                  rule
                )} rounded-full flex items-center justify-center font-bold text-xs text-gray-200 overflow-hidden`}
              >
                {parameter?.shortName || '-'}
              </div>
              <div>
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={onClick}
                  onClick={onClick}
                  className="text-gray-300 text-sm font-medium cursor-pointer"
                >
                  {alert.name}
                  {alertValueText(alert) !== null ? (
                    <span>
                      {' '}
                      |{' '}
                      <span className={getCSSClassBasedOnPriority(priority, 'text')}>
                        {alertValueText(alert)}{' '}
                        {userConfigDataV2?.config &&
                          userConfigDataV2?.config.unit &&
                          (alert.parameter as any) !== 'sensor_status' &&
                          userConfigDataV2?.config.unit[alert.parameter]}
                      </span>
                    </span>
                  ) : (
                    ''
                  )}
                  {/* {userConfigDataV2?.config && userConfigDataV2?.config.unit[alert.parameter]} */}
                </div>
                <div className="justify-start items-center inline-flex text-gray-500 text-xs font-normal">
                  #{alert.alertId}
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-300">
              {moment(alert.timestamp).tz(timeZone).format('HH:mm a')} -{' '}
              {alert.endDate ? moment(alert.endDate).tz(timeZone).format('HH:mm a') : 'Ongoing'}
              <div className="text-xs text-gray-500 mt-1">
                {moment(alert.timestamp).tz(timeZone).format('DD-MM-YYYY')}
              </div>
            </div>
          </div>
          {alert.status === 'pending' && (
            <div className="gap-2 flex">
              <Button
                onClick={(e) => handleUpdateAlert(e, 'dismiss')}
                name="Dismiss"
                size="sm"
                theme="secondary-error"
              />
              <Button
                onClick={(e) => handleUpdateAlert(e, 'resolved')}
                name="Mark as Resolved"
                size="sm"
                theme="secondary-success"
              />
            </div>
          )}
        </div>
        {analysisLoading ? (
          <div className="relative h-36 w-full flex justify-center items-center">
            <Loader />
          </div>
        ) : (
          lineChartGraphData && (
            <AlertGraph
              deviceData={lineChartGraphData}
              boxPoints={{
                start: alert.timestamp && new Date(alert.timestamp),
                end: alert.endDate && new Date(alert.endDate)
              }}
              hideTicks={false}
              dashed
              scatterPoint={alert.avgValue}
              scatterPointLabel={`${alert.serialNumber}- ${alert.name}`}
              scatterPointTime={alert.timestamp && new Date(alert.timestamp)}
            />
          )
        )}
        <div className="flex justify-end mt-3 text-primary-500 items-center cursor-pointer text-sm">
          <div>View Analytics</div>
          <div className="material-icons-outlined text-xl">arrow_forward</div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="text-gray-300 text-sm font-medium">{alert.name}</div>
          <div className="flex items-center gap-3">
            <div className="text-gray-500">Issue status</div>
            <div
              className={clsx('font-medium', {
                'text-warning-500': alert.status === 'pending',
                'text-success-500': alert.status === 'resolved',
                'text-error-500': alert.status === 'dismiss'
              })}
            >
              <p className="text-center text-xs">
                {alertStatus[alert.status.toLowerCase() as AlertStatus]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-gray-500">Assigned to</div>
            <AssignUsers alert={alert} />
          </div>
        </div>
        {/* eslint-disable-next-line */}
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="flex w-full gap-4">
              <div className="border border-gray-600 rounded-lg p-4 mt-3 flex flex-1 flex-col">
                <div className="text-gray-300 font-medium">Tags</div>
                <MultiSelectInputBox
                  availableOptions={tagList}
                  ref={tagInputRef}
                  chips={reasonChips}
                  setChips={setReasonChips}
                  acceptsSentence={false}
                  hideBorder
                  backgroundColor="#12151F"
                />
                <div className="flex justify-end mt-3">
                  {isThereANewTag && (
                    <Button
                      loading={loading}
                      theme="primary"
                      size="sm"
                      name="Save"
                      buttonType="submit"
                    />
                  )}
                </div>
              </div>
              <div className="border border-gray-600 rounded-lg p-4 mt-3 flex flex-col flex-1">
                <div className="text-gray-300 font-medium">Comments</div>
                <CustomTextArea
                  backgroundColor="#12151F"
                  hideBorder
                  height={160}
                  label=""
                  idname="comment"
                />
                <div className="flex justify-end mt-3">
                  {watch('comment')?.length !== 0 && (
                    <Button
                      loading={loading}
                      theme="primary"
                      size="sm"
                      name="Save"
                      buttonType="submit"
                    />
                  )}
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
